<template>
  <div>
   
    <!-- BODY -->
    <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
      <!-- Form -->
      <b-form @submit.prevent="handleSubmit(handleQuestionSubmit)" @reset.prevent="resetForm">
        <!-- Username -->
        <validation-provider #default="validationContext" :name="$t('survey.question')" rules="required" vid="question"
          :custom-messages="{ required: $t('survey.validation.required', { _field_: $t('survey.question') }) }">
          <b-form-group :label="$t('survey.question')" label-for="SurveyCode"
            :state="getValidationState(validationContext)">
            <input v-model="questionText" type="hidden" style="display: none;" />
            <div
              :class="{ 'is-invalid': validationContext.errors[0], 'is-valid': validationContext.valid && validationContext.validated }">
              <quill-editor height="100" :options="snowOption" :content="localQuestion.description"
                @change="onEditorChange($event)" />
            </div>

            <div class="invalid-feedback" :class="{ 'd-block': validationContext.errors[0] }">
              {{ validationContext.errors[0] }}
            </div>
          </b-form-group>
        </validation-provider>

        <b-form-group :label="$t('survey.comment')" label-for="SurveyCode">
          <b-form-checkbox v-model="localQuestion.allowComment" value="true">
            {{ $t('survey.surveyquestion.comment') }}
          </b-form-checkbox>
        </b-form-group>

        <b-form-group :label="$t('survey.required.required')" label-for="SurveyCode">
          <b-form-checkbox v-model="localQuestion.required" value="true">
            {{ $t('survey.surveyquestion.required') }}
          </b-form-checkbox>
        </b-form-group>
        <!-- User Role -->

        <validation-provider #default="validationContext" :name="$t('survey.surveyquestion.question_tag')"
          rules="required" vid="questionTag"
          :custom-messages="{ required: $t('survey.validation.required', { _field_: $t('survey.surveyquestion.question_tag') }) }">
          <b-form-group :label="$t('survey.surveyquestion.question_tag')" label-for="user-role"
            :state="getValidationState(validationContext)">
            <input :value="questionTag ? (questionTag.title || questionTag) : null" type="hidden"
              style="display: none;" />
            <div
              :class="{ 'is-invalid': validationContext.errors[0], 'is-valid': validationContext.valid && validationContext.validated }">
              <question-tag-picker :initialtag="questionTag" :survey-info="surveyInfo" :key="surveyChecker"
                @selectionChanged="QuestionTagUpdated" />
            </div>
            <div class="invalid-feedback" :class="{ 'd-block': validationContext.errors[0] }">
              {{ validationContext.errors[0] }}
            </div>
          </b-form-group>
        </validation-provider>

        <!-- Rating Options - Always visible -->
        <b-form-group :label="$t('survey.surveyquestion.value_text_min')" label-for="user-role">
          <b-col cols="12">
            <b-row v-for="(option, index) in ratingOptions" :key="index" class="mb-1 align-items-center no-gutters">
              <b-col cols="1" class="pr-1">
                <b-form-input :value="option.value" disabled class="text-center" :placeholder="String(option.value)"
                  trim />
              </b-col>
              <b-col cols="auto" class="flex-grow-1 pr-1">
                <validation-provider #default="validationContext"
                  :name="`${$t('survey.surveyquestion.option')} ${option.value}`" rules="required"
                  :vid="`ratingOption${index}`"
                  :custom-messages="{ required: $t('survey.validation.required', { _field_: $t('survey.surveyquestion.option') + ' ' + option.value }) }">
                  <b-form-input v-model="option.text" :placeholder="getDefaultPlaceholder(option.value)"
                    :state="getValidationState(validationContext)" trim />
                  <div class="invalid-feedback" :class="{ 'd-block': validationContext.errors[0] }">
                    {{ validationContext.errors[0] }}
                  </div>
                </validation-provider>
              </b-col>
              <b-col cols="auto" v-if="ratingOptions.length > 2" class="pr-1">
                <b-button variant="outline-danger" size="sm" @click="removeRatingOption(index)">
                  <feather-icon icon="XIcon" size="14" />
                </b-button>
              </b-col>
              <b-col cols="auto" class="pl-0" style="max-width: 280px; overflow: hidden;">
                <div v-if="option.linkedQuestion" class="question-link-display d-flex align-items-center"
                  style="width: 250px; max-width: 250px; overflow: hidden;">
                  <b-badge variant="primary" class="mr-1 flex-grow-1 question-tag-badge" style="max-width: 200px;"
                    :title="getLinkedQuestionTag(option.linkedQuestion)">
                    <span class="d-inline-block text-truncate" style="max-width: 100%;">
                      {{ getLinkedQuestionTag(option.linkedQuestion) }}
                    </span>
                  </b-badge>

                  <b-button variant="outline-danger" size="sm" class="ml-1 flex-shrink-0"
                    @click="clearLinkedQuestion(index)" :title="$t('survey.surveyquestion.remove_link')">
                    <feather-icon icon="XIcon" size="12" />
                  </b-button>
                </div>
                <b-button v-else variant="outline-success" size="sm" @click="openQuestionPicker(index)"
                  :title="$t('survey.surveyquestion.link_question')">
                  <feather-icon icon="ArrowRightIcon" size="14" />
                </b-button>
              </b-col>
            </b-row>

            <b-row v-if="ratingOptions.length < 10" class="mt-2">
              <b-col>
                <b-button variant="outline-primary" size="sm" block @click="addRatingOption">
                  <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                  {{ $t('survey.surveyquestion.add_option') }}
                </b-button>
              </b-col>

            </b-row>
          </b-col>
        </b-form-group>


        <!-- Form Actions -->
        <div class="d-flex mt-2">
          <b-button v-ripple.400="'rgba(255, 255, 255, 0.15)'" variant="primary" class="mr-2" type="submit">
            {{ $t("Update") }}
          </b-button>
          <b-button v-ripple.400="'rgba(186, 191, 199, 0.15)'" type="button" variant="outline-secondary"
            @click="goBack">
            {{ $t("Cancel") }}
          </b-button>
        </div>
      </b-form>
    </validation-observer>

    <!-- Question Picker Modal -->
    <b-modal id="question-picker-modal" ref="questionPickerModal" :title="$t('survey.surveyquestion.select_question')"
      :ok-title="$t('Select')" :cancel-title="$t('Cancel')" ok-variant="primary" cancel-variant="outline-secondary"
      @ok="handleQuestionSelect" @hidden="resetQuestionPicker" size="lg" @shown="onModalShown">
      <question-picker ref="questionPicker" :survey-id="getSurveyId()" :survey-info="surveyInfo"
        :initial-question="selectedQuestionForPicker" @selectionChanged="onQuestionPickerChanged" />
    </b-modal>
  </div>
</template>

<script>
import {
  BFormCheckbox,
  BRow,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BButton,
  BModal,
  BBadge,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required } from "@validations";
import Ripple from "vue-ripple-directive";
import store from "@/store";
import router from "@/router";
import userStoreModule from "../surveyquestionStoreModule";

import { quillEditor } from "vue-quill-editor";
import QuestionTagPicker from "../components/QuestionTagPicker.vue";
import QuestionPicker from "../components/QuestionPicker.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

import { heightTransition } from "@core/mixins/ui/transition";
export default {
  components: {
    BFormCheckbox,
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    BModal,
    BBadge,
    QuestionTagPicker,
    QuestionPicker,
    quillEditor,
    ToastificationContent,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  props: {},
  mixins: [heightTransition],
  data() {
    return {
      required,
      surveyChecker: 0,
      questionTag: null,
      questionText: "",
      isUpdated: false,
      surveyData: null,
      currentPickerIndex: null,
      selectedQuestionForPicker: null,
      allQuestions: [], // Store all questions to find by ID
      isLoadingQuestionData: false, // Flag to prevent watch from interfering during load
      ratingOptions: [
        { value: 1, text: '', linkedQuestion: null },
        { value: 2, text: '', linkedQuestion: null },
        { value: 3, text: '', linkedQuestion: null },
        { value: 4, text: '', linkedQuestion: null },
        { value: 5, text: '', linkedQuestion: null }
      ],
      snowOption: {
        theme: "snow",
        modules: {
          toolbar: [
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ align: [] }],
            [{ color: [] }, { background: [] }],
            ["clean"],
          ],
        },
      },
      localQuestion: {
        required: true,
        description: "",
        questionType: "",
        questionTypeKey: "",
        allowComment: false,
        questionAnswers: [],
        additionalInfo: {
          min1Value: 1,
          min2Value: 2,
          min3Value: 3,
          min4Value: 4,
          min5Value: 5,
          min1Text: "Poor",
          min2Text: "Needs Improvement",
          min3Text: "Average",
          min4Text: "Good",
          min5Text: "Excellent",
          maxValue: 5,
          minText: "Bad",
          maxText: "Amazing",
        },
      },
    };
  },
  mounted() {
    this.initTrHeight();
  },
  created() {
    window.addEventListener("resize", this.initTrHeight);

    // Get questionData from store
    const USER_APP_STORE_MODULE_NAME = "app-survey-questions";

    // Register module if not already registered
    if (!store.hasModule(USER_APP_STORE_MODULE_NAME)) {
      store.registerModule(USER_APP_STORE_MODULE_NAME, userStoreModule);
    }

    const questionFromStore = store.getters["app-survey-questions/getCurrentQuestion"];

    // Load survey info from route query first (to load allQuestions)
    const surveyId = router.currentRoute.query.s;
    if (surveyId) {
      this.loadSurveyInfo(surveyId);
    }

    // Load question data from store after survey info is loaded
    if (questionFromStore) {
      // Wait a bit for allQuestions to load
      this.$nextTick(() => {
        setTimeout(() => {
          this.loadQuestionData(questionFromStore);
        }, 300);
      });
    }
  },
  destroyed() {
    window.removeEventListener("resize", this.initTrHeight);
  },
  computed: {
    surveyInfo() {
      // Get survey info from route query or store
      const surveyId = router.currentRoute.query.s || store.getters["app/currentUnit"];
      if (surveyId && this.surveyData) {
        return this.surveyData;
      }
      return null;
    },
  },
  watch: {
    ratingOptions: {
      handler(newVal) {
        // Don't sync during initial load to prevent overwriting linkedQuestions
        if (!this.isLoadingQuestionData) {
          // Auto-sync rating options to additionalInfo when changed
          this.syncRatingOptionsToAdditionalInfo();
        }
      },
      deep: true
    },
  },
  methods: {
    getValidationState({ dirty, validated, valid = null }) {
      return dirty || validated ? valid : null
    },

    // Rating options methods
    addRatingOption() {
      if (this.ratingOptions.length < 10) {
        const newValue = this.ratingOptions.length + 1;
        this.ratingOptions.push({ value: newValue, text: '', linkedQuestion: null });
        this.syncRatingOptionsToAdditionalInfo();
      }
    },

    removeRatingOption(index) {
      if (this.ratingOptions.length > 2) {
        this.ratingOptions.splice(index, 1);
        // Re-index values
        this.ratingOptions.forEach((option, idx) => {
          option.value = idx + 1;
        });
        this.syncRatingOptionsToAdditionalInfo();
      }
    },

    getDefaultPlaceholder(value) {
      const placeholders = {
        1: 'Poor',
        2: 'Needs Improvement',
        3: 'Average',
        4: 'Good',
        5: 'Excellent'
      };
      return placeholders[value] || `Option ${value}`;
    },

    syncRatingOptionsToAdditionalInfo() {
      // Convert ratingOptions array to additionalInfo object format
      const info = {};

      // Add min values and texts
      this.ratingOptions.forEach((option, index) => {
        const key = index + 1;
        info[`min${key}Value`] = option.value;
        info[`min${key}Text`] = option.text;
      });

      // Add additional fields
      info.maxValue = this.ratingOptions.length;
      info.minText = this.ratingOptions[0]?.text || "Bad";
      info.maxText = this.ratingOptions[this.ratingOptions.length - 1]?.text || "Amazing";

      this.localQuestion.additionalInfo = info;

      // Store linked questions directly in localQuestion, not in additionalInfo
      // Format: { "0": questionId, "1": questionId, ... }
      const linkedQuestions = {};
      this.ratingOptions.forEach((option, index) => {
        if (option.linkedQuestion) {
          linkedQuestions[String(index)] = option.linkedQuestion;
        }
      });

      // Update linkedQuestions based on current ratingOptions state
      if (Object.keys(linkedQuestions).length > 0) {
        this.localQuestion.linkedQuestions = linkedQuestions;
      } else {
        // If no linked questions in ratingOptions, always set to null
        this.localQuestion.linkedQuestions = null;
      }
    },

    loadRatingOptionsFromAdditionalInfo(additionalInfo) {
      // Convert additionalInfo object to ratingOptions array
      const options = [];
      let index = 1;
      while (additionalInfo[`min${index}Value`] !== undefined) {
        options.push({
          value: additionalInfo[`min${index}Value`] || index,
          text: additionalInfo[`min${index}Text`] || '',
          linkedQuestion: null // Initialize linkedQuestion property
        });
        index++;
      }
      // If no options found, initialize with default 5 options
      if (options.length === 0) {
        options.push(
          { value: 1, text: '', linkedQuestion: null },
          { value: 2, text: '', linkedQuestion: null },
          { value: 3, text: '', linkedQuestion: null },
          { value: 4, text: '', linkedQuestion: null },
          { value: 5, text: '', linkedQuestion: null }
        );
      }
      this.ratingOptions = options;
    },
    resetForm() {
      this.questionText = '';
      this.questionTag = null;
      // Reset rating options to default 5 options
      this.ratingOptions = [
        { value: 1, text: '', linkedQuestion: null },
        { value: 2, text: '', linkedQuestion: null },
        { value: 3, text: '', linkedQuestion: null },
        { value: 4, text: '', linkedQuestion: null },
        { value: 5, text: '', linkedQuestion: null }
      ];
    },
    getSurveyId() {
      return router.currentRoute.query.s || store.getters["app/currentUnit"];
    },
    openQuestionPicker(optionIndex) {
      this.currentPickerIndex = optionIndex;
      const linkedQuestionId = this.ratingOptions[optionIndex]?.linkedQuestion;

      // If linkedQuestion is just an ID, find the full question object
      if (linkedQuestionId) {
        if (typeof linkedQuestionId === 'number' || typeof linkedQuestionId === 'string') {
          // It's an ID, find the question
          this.selectedQuestionForPicker = this.allQuestions.find(q => q.id == linkedQuestionId) || null;
        } else {
          // It's already an object
          this.selectedQuestionForPicker = linkedQuestionId;
        }
      } else {
        this.selectedQuestionForPicker = null;
      }

      this.$nextTick(() => {
        this.$refs.questionPickerModal.show();
      });
    },
    handleQuestionSelect(bvModalEvt) {
      bvModalEvt.preventDefault();
      if (this.currentPickerIndex !== null && this.selectedQuestionForPicker) {
        this.onQuestionSelected(this.currentPickerIndex, this.selectedQuestionForPicker);
        this.$nextTick(() => {
          this.$refs.questionPickerModal.hide();
        });
      }
    },
    onQuestionPickerChanged(question) {
      this.selectedQuestionForPicker = question;
      // Auto-close modal when question is selected
      if (question && this.currentPickerIndex !== null) {
        this.$nextTick(() => {
          this.onQuestionSelected(this.currentPickerIndex, question);
          this.$refs.questionPickerModal.hide();
        });
      }
    },
    onModalShown() {
      // Set current selection when modal is shown
      const linkedQuestionId = this.ratingOptions[this.currentPickerIndex]?.linkedQuestion;
      if (linkedQuestionId) {
        if (typeof linkedQuestionId === 'number' || typeof linkedQuestionId === 'string') {
          this.selectedQuestionForPicker = this.allQuestions.find(q => q.id == linkedQuestionId) || null;
        } else {
          this.selectedQuestionForPicker = linkedQuestionId;
        }
      } else {
        this.selectedQuestionForPicker = null;
      }
    },
    resetQuestionPicker() {
      this.currentPickerIndex = null;
      this.selectedQuestionForPicker = null;
    },
    onQuestionSelected(optionIndex, question) {
      if (this.ratingOptions[optionIndex]) {
        if (question) {
          // Store only the question ID
          this.$set(this.ratingOptions[optionIndex], 'linkedQuestion', question.id);
        } else {
          // If question is null, clear the linked question
          this.$set(this.ratingOptions[optionIndex], 'linkedQuestion', null);
        }
      }
    },
    clearLinkedQuestion(optionIndex) {
      if (this.ratingOptions[optionIndex]) {
        this.$set(this.ratingOptions[optionIndex], 'linkedQuestion', null);
        this.syncRatingOptionsToAdditionalInfo();
      }
    },
    getQuestionById(questionId) {
      if (!questionId) return null;

      // If it's already an object, return it
      if (typeof questionId === 'object') {
        return questionId;
      }

      // If it's an ID, find the question
      return this.allQuestions.find(q => q.id == questionId) || null;
    },
    getQuestionTitle(questionId) {
      if (!questionId) return '';

      // If it's already an object with description
      if (typeof questionId === 'object' && questionId.description) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = questionId.description;
        return (tempDiv.textContent || tempDiv.innerText || '').trim();
      }

      // If it's an ID, find the question
      const question = this.allQuestions.find(q => q.id == questionId);
      if (question && question.description) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = question.description;
        return (tempDiv.textContent || tempDiv.innerText || '').trim();
      }

      return '';
    },
    getLinkedQuestionTag(linkedQuestionId) {
      if (!linkedQuestionId) return '';

      // If it's an ID, find the question
      const question = this.getQuestionById(linkedQuestionId);

      if (question && question.questionTag) {
        // questionTag can be a string or an object with title property
        if (typeof question.questionTag === 'object' && question.questionTag.title) {
          return question.questionTag.title;
        }
        return question.questionTag;
      }

      return '';
    },
    initTrHeight() {
      this.trSetHeight(null);
      this.$nextTick(() => {
        // this.trSetHeight(this.$refs.form.scrollHeight)
      });
    },
    goBack() {
      const surveyId = router.currentRoute.query.s || store.getters["app/currentUnit"];
      router.push({ name: 'SurveyQuestions', query: { s: surveyId } });
    },
    handleQuestionSubmit() {
      // Sync rating options to additionalInfo before submitting
      this.syncRatingOptionsToAdditionalInfo();

      const surveyId = router.currentRoute.query.s || store.getters["app/currentUnit"];
      this.localQuestion.surveyid = surveyId;

      // Set question type to Range Selection for all surveys
      this.localQuestion.questionType = "Range Question";
      this.localQuestion.questionTypeKey = "RANGE_SELECTION";

      // Update question
      const action = store.dispatch("app-survey-questions/updatequestion", this.localQuestion);

      action
        .then(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('Success'),
              icon: 'CheckIcon',
              variant: 'success',
            },
          });

          // Navigate back to questions list
          router.push({ name: 'SurveyQuestions', query: { s: surveyId } });
        })
        .catch((error) => {
          console.error("Error saving survey question:", error);

          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('survey.surveyattendee.error'),
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: this.$t(`${error.response?.data?.message}`) || this.$t(`${error.message}`) || this.$t('survey.error_adding_question')
            },
          });
        });
    },

    QuestionTagUpdated(payload) {
      //console.log("question result in info ");
      //console.log(payload);
      if (payload == null || payload == "" || payload == undefined) {
        this.questionTag = null;
        this.questionTagType = null;
        (this.localQuestion.questionTag = null),
          (this.localQuestion.questionTagKey = null);
      } else {
        //console.log("question type  data updated ");
        //console.log(payload.typekey);

        this.questionTagType = payload.typekey;
        this.questionTag = payload.title;
        this.questionTag = payload;

        (this.localQuestion.questionTag = payload.title),
          (this.localQuestion.questionTagType = payload.typekey);
      }
    },

    onEditorChange({ quill, html, text }) {
      //     //console.log('editor change!', quill, html, text)
      this.isUpdated = true;
      this.localQuestion.description = html;
      this.questionText = text.trim();
    },
    loadQuestionData(question) {
      if (!question) return;

      // Set flag to prevent watch from interfering
      this.isLoadingQuestionData = true;

      this.localQuestion = JSON.parse(JSON.stringify(question));

      // Parse questionAnswers if it's a string
      if (typeof question.questionAnswers === 'string') {
        this.localQuestion.questionAnswers = JSON.parse(question.questionAnswers);
      }

      // Parse additionalInfo if it's a string
      if (typeof question.additionalInfo === 'string') {
        this.localQuestion.additionalInfo = JSON.parse(question.additionalInfo);
      }

      // Parse linkedQuestions if it's a string
      if (typeof question.linkedQuestions === 'string' && question.linkedQuestions.trim() !== '') {
        try {
          this.localQuestion.linkedQuestions = JSON.parse(question.linkedQuestions);
        } catch (e) {
          console.error('Error parsing linkedQuestions:', e, question.linkedQuestions);
          this.localQuestion.linkedQuestions = null;
        }
      } else if (question.linkedQuestions && typeof question.linkedQuestions === 'object') {
        this.localQuestion.linkedQuestions = question.linkedQuestions;
      } else {
        this.localQuestion.linkedQuestions = null;
      }

      this.questionTag = question.questionTag;

      // Set questionText from description
      if (question.description) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = question.description;
        this.questionText = (tempDiv.textContent || tempDiv.innerText || '').trim();
      } else {
        this.questionText = '';
      }

      // Load rating options from additionalInfo first
      if (this.localQuestion.additionalInfo) {
        this.loadRatingOptionsFromAdditionalInfo(this.localQuestion.additionalInfo);
      }

      // Load linked questions from localQuestion.linkedQuestions
      // This must be called AFTER loadRatingOptionsFromAdditionalInfo to ensure ratingOptions array exists
      if (this.localQuestion.linkedQuestions && Object.keys(this.localQuestion.linkedQuestions).length > 0) {
        this.$nextTick(() => {
          this.loadLinkedQuestions(this.localQuestion.linkedQuestions);
          // Re-enable watch after loading is complete
          this.$nextTick(() => {
            this.isLoadingQuestionData = false;
          });
        });
      } else {
        // Re-enable watch if no linkedQuestions to load
        this.$nextTick(() => {
          this.isLoadingQuestionData = false;
        });
      }
    },
    loadLinkedQuestions(linkedQuestions) {
      // linkedQuestions is an object like { "0": questionId, "1": questionId, ... }
      if (typeof linkedQuestions === 'string') {
        try {
          linkedQuestions = JSON.parse(linkedQuestions);
        } catch (e) {
          console.error('Error parsing linkedQuestions in loadLinkedQuestions:', e);
          return;
        }
      }

      if (!linkedQuestions || typeof linkedQuestions !== 'object') {
        console.warn('linkedQuestions is not a valid object:', linkedQuestions);
        return;
      }

      // Ensure ratingOptions array exists and has the correct length
      if (!this.ratingOptions || this.ratingOptions.length === 0) {
        console.warn('ratingOptions is empty, cannot load linked questions');
        return;
      }


      Object.keys(linkedQuestions).forEach((key) => {
        // Key format: "0", "1", "2", ... (numeric string)
        if (!/^\d+$/.test(key)) {
          console.warn('Invalid key in linkedQuestions (must be numeric):', key);
          return;
        }

        const arrayIndex = parseInt(key);
        const questionId = linkedQuestions[key];

        if (this.ratingOptions[arrayIndex] && questionId) {
          this.$set(this.ratingOptions[arrayIndex], 'linkedQuestion', questionId);
        } else {
          console.warn(`Cannot set linkedQuestion: index=${arrayIndex}, questionId=${questionId}, ratingOptions[${arrayIndex}]=`, this.ratingOptions[arrayIndex]);
        }
      });
    },
    loadSurveyInfo(surveyId) {
      const USER_APP_STORE_MODULE_NAME = "app-survey-questions";

      if (!store.hasModule(USER_APP_STORE_MODULE_NAME)) {
        store.registerModule(USER_APP_STORE_MODULE_NAME, userStoreModule);
      }

      store
        .dispatch("app-survey-questions/fetchCourse", { id: surveyId })
        .then((response) => {
          this.surveyData = response.data.data;
          this.surveyChecker = this.surveyChecker + 1;
        })
        .catch((error) => {
          console.error("Error loading survey info:", error);
        });

      // Load all questions for the picker
      this.loadAllQuestions(surveyId);
    },
    loadAllQuestions(surveyId) {
      const USER_APP_STORE_MODULE_NAME = "app-survey-questions";

      if (!store.hasModule(USER_APP_STORE_MODULE_NAME)) {
        store.registerModule(USER_APP_STORE_MODULE_NAME, userStoreModule);
      }

      store
        .dispatch("app-survey-questions/fetchSurveyQuestions", {
          id: surveyId,
          l: 1000, // Get all questions
          p: 1,
        })
        .then((response) => {
          this.allQuestions = response.data.data || [];
        })
        .catch((error) => {
          console.error("Error loading questions:", error);
        });
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/quill.scss";

.ql-editor {
  min-height: 100px;
}

.is-invalid .quill-editor .ql-container {
  border-color: #ea5455 !important;
}

.is-invalid .quill-editor .ql-toolbar {
  border-color: #ea5455 !important;
}

.is-valid .quill-editor .ql-container {
  border-color: #28c76f !important;
}

.is-valid .quill-editor .ql-toolbar {
  border-color: #28c76f !important;
}

// Question Tag Picker validation styles
.is-invalid .v-select .vs__dropdown-toggle {
  border-color: #ea5455 !important;
}

.is-valid .v-select .vs__dropdown-toggle {
  border-color: #28c76f !important;
}

// Question Tag Badge styles
.question-tag-badge {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
  min-width: 0;
  display: inline-block;

  span {
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
